import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGetDocumentAiResults,
  useGetDocumentAiStatus,
} from "src/generated/hooks/avela-ai/queries/queries";
import {
  DocumentAIResultsResponse,
  DocumentAIStatusResponse,
  DocumentAIValidationResult,
} from "src/generated/hooks/avela-ai/requests/types.gen";
import * as RemoteData from "../types/remoteData";
import { exponentialBackoff } from "./useDynamicPolling";

// Re-export types for backwards compatibility
export type AIValidationResult = DocumentAIValidationResult;
export type DocumentAIStatus = DocumentAIStatusResponse;
export type DocumentAIResults = DocumentAIResultsResponse;

// Exponential backoff for 404s (document not ready yet)
const RETRY_404_INTERVAL = exponentialBackoff(2_000, 1.5, 15_000);

// Exponential backoff for processing states (AI still working)
const RETRY_PROCESSING_INTERVAL = exponentialBackoff(3_000, 1.2, 10_000);

// Retry limits - adjust these to change retry behavior
const MAX_404_RETRIES = 25;
const MAX_PROCESSING_RETRIES = 40;

// Processing statuses that indicate the document is still being processed
const PROCESSING_STATUSES = [
  "PENDING",
  "PROCESSING_OCR",
  "PROCESSING_LLM",
  "REPROCESSING_LLM",
] as const;

// COMPLETED / FAILED / ERROR are terminal — stop polling
const isProcessingStatus = (status: string) =>
  PROCESSING_STATUSES.some((processingStatus) => processingStatus === status);

/**
 * Hook for fetching DocumentAI status with RemoteData pattern.
 * Automatically fetches status when documentMetadataId changes.
 *
 * @param documentMetadataId - UUID from DocumentMetadata.document_id or null to skip
 * @returns RemoteData wrapper with status data and refetch function
 */
export const useDocumentAIStatus = (documentMetadataId: string | null) => {
  const queryResult = useGetDocumentAiStatus(
    { path: { document_id: documentMetadataId ?? "" } },
    undefined,
    {
      enabled: !!documentMetadataId,
      retry: false,
    }
  );

  const statusData = documentMetadataId
    ? RemoteData.fromRestResult<unknown, DocumentAIStatus>({
        data: queryResult.data,
        error: queryResult.error,
        isLoading: queryResult.isLoading,
        isRefetching: queryResult.isRefetching,
        isError: queryResult.isError,
      })
    : RemoteData.notAsked<unknown, DocumentAIStatus>();

  return {
    statusData,
    isLoading: queryResult.isLoading,
    refetch: queryResult.refetch,
  };
};

/**
 * Hook for fetching DocumentAI results with RemoteData pattern.
 * Automatically fetches results when documentMetadataId changes.
 * Includes auto-retry logic for 404 responses (document not yet processed).
 *
 * @param documentMetadataId - UUID from DocumentMetadata.document_id or null to skip
 * @returns RemoteData wrapper with results data and refetch function
 */
export const useDocumentAIResults = (documentMetadataId: string | null) => {
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentInterval404Ref = useRef<number | undefined>(undefined);
  const currentIntervalProcessingRef = useRef<number | undefined>(undefined);

  const queryResult = useGetDocumentAiResults(
    { path: { document_id: documentMetadataId ?? "" } },
    undefined,
    {
      enabled: !!documentMetadataId,
      retry: false,
    }
  );

  const { data, isLoading, error, refetch } = queryResult;

  // the API will return a 404 with response
  // { error: "not_found", message: "No AI processing record found for this document" }
  // detect this for retry logic
  const is404Error =
    !!error &&
    typeof error === "object" &&
    "error" in error &&
    error.error === "not_found";

  // Handle retry logic for processing states and 404s
  useEffect(() => {
    if (!documentMetadataId) {
      setRetryCount(0);
      retryTimeoutRef.current = null;
      currentInterval404Ref.current = undefined;
      currentIntervalProcessingRef.current = undefined;
      return;
    }

    // Poll only while actively processing (not COMPLETED / FAILED / ERROR)
    if (data && isProcessingStatus(data.status)) {
      const shouldContinuePolling = retryCount < MAX_PROCESSING_RETRIES;

      if (shouldContinuePolling) {
        const delayMs = RETRY_PROCESSING_INTERVAL(
          currentIntervalProcessingRef.current
        );
        currentIntervalProcessingRef.current = delayMs;

        const timeoutId = setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          refetch();
        }, delayMs);

        retryTimeoutRef.current = timeoutId;
      } else {
        console.log(
          `[DocumentAI] Processing retry limit reached for ${documentMetadataId}`,
          {
            status: data.status,
            maxRetries: MAX_PROCESSING_RETRIES,
            finalAttempt: retryCount,
          }
        );
        currentIntervalProcessingRef.current = undefined;
      }
    } else if (data && data.status === "COMPLETED") {
      console.log(
        `[DocumentAI] Processing completed for ${documentMetadataId}`,
        {
          status: data.status,
          totalRetries: retryCount,
        }
      );
      setRetryCount(0);
      currentInterval404Ref.current = undefined;
      currentIntervalProcessingRef.current = undefined;
    }

    // Handle 404 errors (document not ready yet)
    if (is404Error && !data) {
      const shouldRetry = retryCount < MAX_404_RETRIES;

      if (shouldRetry) {
        const delayMs = RETRY_404_INTERVAL(currentInterval404Ref.current);
        currentInterval404Ref.current = delayMs;

        const timeoutId = setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          refetch();
        }, delayMs);

        retryTimeoutRef.current = timeoutId;
      } else {
        console.log(
          `[DocumentAI] 404 retry limit reached for ${documentMetadataId}`,
          {
            maxRetries: MAX_404_RETRIES,
            finalAttempt: retryCount,
            reason: "Document not found after max retries",
          }
        );
        currentInterval404Ref.current = undefined;
      }
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentMetadataId, data, error, retryCount, refetch]);

  // Custom RemoteData logic to handle retry states
  // During retry, show loading state and suppress errors
  // We use condition-based logic instead of timeout ref because cleanup runs before render
  const isRetrying404 = is404Error && !data && retryCount < MAX_404_RETRIES;
  const isRetryingProcessing =
    data &&
    isProcessingStatus(data.status) &&
    retryCount < MAX_PROCESSING_RETRIES;
  const isRetrying = isRetrying404 || isRetryingProcessing;

  const resultsData = (() => {
    if (!documentMetadataId) {
      return RemoteData.notAsked<unknown, DocumentAIResults>();
    }

    // While retrying (404s or processing), show loading state and suppress errors
    if (isRetrying) {
      return RemoteData.loading<unknown, DocumentAIResults>();
    }

    return RemoteData.fromRestResult<unknown, DocumentAIResults>({
      data: data,
      error: error,
      isLoading,
      isRefetching: queryResult.isRefetching,
      isError: queryResult.isError,
    });
  })();

  const handleRefetch = useCallback(() => {
    setRetryCount(0);
    retryTimeoutRef.current = null;
    currentInterval404Ref.current = undefined;
    currentIntervalProcessingRef.current = undefined;
    refetch();
  }, [refetch]);

  return {
    resultsData,
    isLoading: isLoading || isRetrying, // Show loading while retrying
    refetch: handleRefetch, // Reset retry count on manual refetch
    retryCount, // Expose retry count for debugging/UI purposes
  };
};

/**
 * Hook for polling DocumentAI status until processing is complete.
 * Automatically stops polling when status reaches a terminal state or timeout is reached.
 *
 * @param documentMetadataId - UUID from DocumentMetadata.document_id
 * @param intervalMs - Polling interval in milliseconds (default: 5000)
 * @param shouldPoll - Whether polling should be active (default: true)
 * @param timeoutMs - Maximum polling duration in milliseconds (default: 15 minutes)
 * @returns Status data, refetch function, and polling state
 */
export const useDocumentAIPolling = (
  documentMetadataId: string | null,
  intervalMs: number = 5000,
  shouldPoll: boolean = true,
  timeoutMs: number = 15 * 60 * 1000 // 15 minutes
) => {
  const { statusData, refetch } = useDocumentAIStatus(documentMetadataId);
  const intervalRef = useRef<number | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Extract status value to use as dependency instead of the whole RemoteData object
  const currentStatus = statusData.hasData()
    ? statusData.toNullable()?.status
    : undefined;

  useEffect(() => {
    // Clear any existing interval when dependencies change
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!shouldPoll || !documentMetadataId) {
      setIsPolling(false);
      return;
    }

    // Check if document is still processing
    const isProcessing =
      currentStatus !== undefined &&
      PROCESSING_STATUSES.includes(
        currentStatus as (typeof PROCESSING_STATUSES)[number]
      );

    if (!isProcessing) {
      setIsPolling(false);
      return;
    }

    // Start polling
    intervalRef.current = window.setInterval(refetch, intervalMs);
    setIsPolling(true);

    // Set timeout to stop polling after timeoutMs
    const timeoutId = setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsPolling(false);
      }
    }, timeoutMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      clearTimeout(timeoutId);
      setIsPolling(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentMetadataId, shouldPoll, intervalMs, timeoutMs, currentStatus]);

  return {
    statusData,
    refetch,
    isPolling,
  };
};
